import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { drawLine } from './draw-fns'
import { groupByCategory, groupKitchenItems, KitchenGroup, kitchenMarkPlacement, noteDisplay, waiterSeats } from './tableTicket'

type CanvasMode = 'standard' | 'price' | 'item' | 'category'

function finishCanvas(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, y: number) {
  const data = ctx.getImageData(0, 0, 576, Math.max(y, 32))
  canvas.height = Math.max(y - 16, 32)
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.putImageData(data, 0, 0)
  return canvas
}

export function drawEnBoiteBanner(ctx: CanvasRenderingContext2D, y: number, fontSizeFactor: number) {
  const bannerSize = Math.round(36 * fontSizeFactor)
  ctx.save()
  ctx.fillStyle = '#000000'
  ctx.textAlign = 'left'
  ctx.font = `bold ${bannerSize}px Arial`
  ctx.fillText('Pour Emporter', 12, y + bannerSize)
  ctx.restore()
  return y + bannerSize + Math.round(48 * fontSizeFactor)
}

function slipClock(timeZone?: string) {
  try {
    return utcToZonedTime(new Date(), timeZone || 'UTC')
  } catch {
    return new Date()
  }
}

function drawHeader(ctx: CanvasRenderingContext2D, y: number, order: any, store: any, fontSizeFactor: number) {
  const table = String((order?.isTableOrder && order.seatTableName) || order.tableName || '').trim()
  const stamp = slipClock(store?.timeZone)
  const dateText = format(stamp, 'M/d/yyyy')
  const timeText = format(stamp, 'HH:mm')
  if (order?.isTableOrder) {
    const labelSize = Math.max(28, Math.round(32 * fontSizeFactor))
    ctx.font = `bold ${labelSize}px Arial`
    ctx.textAlign = 'left'
    ctx.fillText('DINE-IN', 12, y + labelSize)
    y += labelSize + 8
  }
  let tableSize = Math.round(84 * fontSizeFactor)
  const dateSize = Math.round(28 * fontSizeFactor)
  ctx.textAlign = 'left'
  ctx.font = `bold ${tableSize}px Arial`
  while (tableSize > 48 && ctx.measureText(table).width > 320) {
    tableSize -= 4
    ctx.font = `bold ${tableSize}px Arial`
  }
  const baseline = y + tableSize
  if (table) ctx.fillText(table, 12, baseline)
  const server = String(order?.items?.find((item) => item?.posUserName)?.posUserName || "").trim().slice(0, 10)
  if (server) {
    const tableWidth = table ? ctx.measureText(table).width : 0
    const nameSize = Math.max(18, Math.round(20 * fontSizeFactor))
    ctx.font = `normal ${nameSize}px Arial`
    const nameWidth = ctx.measureText(server).width
    const nameX = Math.min(Math.max(12 + tableWidth + 16, 288 - nameWidth / 2), 430 - nameWidth)
    ctx.textAlign = "left"
    ctx.fillText(server, nameX, baseline)
  }
  ctx.font = `normal ${dateSize}px Arial`
  ctx.textAlign = 'right'
  ctx.fillText(dateText, 564, y + dateSize + 4)
  ctx.fillText(timeText, 564, y + dateSize * 2 + 12)
  ctx.textAlign = 'left'
  const bottom = Math.max(baseline, y + dateSize * 2 + 12) + 12
  drawLine(ctx, bottom)
  ctx.setLineDash([])
  return bottom + 28
}

function drawBoldLeft(ctx: CanvasRenderingContext2D, y: number, fontSize: number, text: string) {
  ctx.font = `bold ${fontSize}px Arial`
  ctx.textAlign = 'left'
  ctx.fillText(text, 16, y)
  return y + Math.round(fontSize * 1.05)
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = String(text || '').split(' ').filter(Boolean)
  if (!words.length) return ['']
  const lines: string[] = []
  let current = words[0]
  for (let i = 1; i < words.length; i++) {
    const next = `${current} ${words[i]}`
    if (ctx.measureText(next).width <= maxWidth) current = next
    else {
      lines.push(current)
      current = words[i]
    }
  }
  lines.push(current)
  return lines
}

function drawGroup(ctx: CanvasRenderingContext2D, lineY: number, group: KitchenGroup, placement: string, fontSize: number, withPrice: boolean) {
  const marks = group.marks.join(' ')
  const place = kitchenMarkPlacement(placement, Boolean(marks))
  const qty = String(group.amount)
  const left = 16
  const rightEdge = 564
  let y = lineY + Math.round(fontSize * 0.9)
  if (place === 'above' && marks) y = drawBoldLeft(ctx, y, fontSize, marks)
  const priceText = withPrice ? `$${(group.price * group.amount).toFixed(2)}` : ''
  ctx.textAlign = 'left'
  if (place === 'beginning' && marks) {
    ctx.font = `bold ${fontSize}px Arial`
    ctx.fillText(marks, left, y)
    const markWidth = ctx.measureText(`${marks}  `).width
    ctx.font = `normal ${fontSize}px Arial`
    const nameMax = rightEdge - left - markWidth - (priceText ? ctx.measureText(priceText).width + 12 : 0)
    const lines = wrapText(ctx, `${qty} ${group.name}`, Math.max(48, nameMax))
    lines.forEach((line, index) => ctx.fillText(line, left + markWidth, y + index * Math.round(fontSize * 1.05)))
    if (priceText) {
      ctx.textAlign = 'right'
      ctx.fillText(priceText, rightEdge, y)
      ctx.textAlign = 'left'
    }
    y += lines.length * Math.round(fontSize * 1.05)
  } else if (place === 'end' && marks) {
    ctx.font = `bold ${fontSize}px Arial`
    const markWidth = ctx.measureText(marks).width
    ctx.font = `normal ${fontSize}px Arial`
    const qtyText = `${qty} `
    const qtyWidth = ctx.measureText(qtyText).width
    const priceWidth = priceText ? ctx.measureText(priceText).width + 12 : 0
    const nameMax = rightEdge - left - qtyWidth - markWidth - priceWidth - 12
    const lines = wrapText(ctx, group.name, Math.max(48, nameMax))
    ctx.fillText(qtyText, left, y)
    lines.forEach((line, index) => ctx.fillText(line, left + qtyWidth, y + index * Math.round(fontSize * 1.05)))
    ctx.font = `bold ${fontSize}px Arial`
    ctx.textAlign = 'right'
    ctx.fillText(marks, rightEdge - priceWidth, y)
    if (priceText) {
      ctx.font = `normal ${fontSize}px Arial`
      ctx.fillText(priceText, rightEdge, y)
    }
    ctx.textAlign = 'left'
    y += lines.length * Math.round(fontSize * 1.05)
  } else {
    const right = priceText
    ctx.font = `normal ${fontSize}px Arial`
    const qtyText = `${qty} `
    const qtyWidth = ctx.measureText(qtyText).width
    const priceWidth = right ? ctx.measureText(right).width + 12 : 0
    const lines = wrapText(ctx, group.name, Math.max(48, rightEdge - left - qtyWidth - priceWidth))
    ctx.fillText(qtyText, left, y)
    lines.forEach((line, index) => ctx.fillText(line, left + qtyWidth, y + index * Math.round(fontSize * 1.05)))
    if (right) {
      ctx.textAlign = 'right'
      ctx.fillText(right, rightEdge, y)
      ctx.textAlign = 'left'
    }
    y += lines.length * Math.round(fontSize * 1.05)
  }
  if (place === 'below' && marks) y = drawBoldLeft(ctx, y, fontSize, marks)
  const optionSize = Math.max(18, fontSize - 4)
  ctx.font = `normal ${optionSize}px Arial`
  ctx.textAlign = 'left'
  group.options.forEach((option) => {
    const lines = wrapText(ctx, option, rightEdge - left - 28)
    lines.forEach((line) => {
      ctx.fillText(`- ${line}`, left + 28, y)
      y += Math.round(optionSize * 1.05)
    })
  })
  if (group.itemNote) {
    ctx.fillText(group.itemNote, left, y)
    y += Math.round(optionSize * 1.05)
  }
  if (group.seatNote) {
    ctx.fillText(group.seatNote, left, y)
    y += Math.round(optionSize * 1.05)
  }
  return y + 2
}

function drawSlip(order: any, store: any, printer: any, groups: KitchenGroup[], withPrice: boolean, category?: string) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return canvas
  const fontSizeFactor = 1 + (printer?.font_increase_percentage || 0) / 100
  canvas.width = 576
  canvas.height = 10000
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#000000'
  let y = 32 + (printer?.empty_lines_on_top || 0) * 16
  y = drawHeader(ctx, y, order, store, fontSizeFactor)
  const tableNote = noteDisplay(order?.tableNote || groups[0]?.item?.tableNote)
  if (tableNote) {
    const noteSize = Math.round(28 * fontSizeFactor)
    ctx.font = `normal ${noteSize}px Arial`
    tableNote.split('\n').forEach((line) => {
      ctx.fillText(line, 10, y)
      y += noteSize + 4
    })
    drawLine(ctx, y)
    y += 16
  }
  if (category) {
    const title = Math.round(32 * fontSizeFactor)
    ctx.font = `bold ${title}px Arial`
    ctx.textAlign = 'center'
    ctx.fillText(category, 288, y)
    ctx.textAlign = 'left'
    y += title * 1.4
  }
  const placement = order?.tableMarkPlacement
  const fontSize = Math.round(32 * fontSizeFactor)
  let seenHeading = ""
  groups.forEach((group) => {
    const heading = group.item?.fireHeadingOnly
      ? String(group.item.name || "")
      : String(group.item?.fireHeading || "")
    if (heading && heading !== seenHeading) {
      seenHeading = heading
      ctx.font = `bold ${fontSize}px Arial`
      ctx.textAlign = 'center'
      ctx.fillText(heading, 288, y + Math.round(fontSize * 0.9))
      ctx.textAlign = 'left'
      y += fontSize + Math.round(fontSize * 0.25)
      drawLine(ctx, y)
      ctx.setLineDash([])
    }
    if (group.item?.fireHeadingOnly) return
    y = drawGroup(ctx, y, group, placement, fontSize, withPrice)
    drawLine(ctx, y)
    ctx.setLineDash([])
  })
  return finishCanvas(canvas, ctx, y + 16)
}

export function renderTableKitchenReceipt(order: any, store: any, printer: any, mode: CanvasMode): HTMLCanvasElement | HTMLCanvasElement[] {
  const tableName = order.seatTableName || order.tableName || ''
  const groups = groupKitchenItems(order.items || [], tableName, true)
  const withPrice = mode === 'price'
  if (mode === 'item') return groups.map((group) => drawSlip(order, store, printer, [group], false))
  if (mode === 'category') {
    return groupByCategory(groups).map(([category, categoryGroups]) => drawSlip(order, store, printer, categoryGroups, false, category))
  }
  return drawSlip(order, store, printer, groups, withPrice)
}

export function renderWaiterReceipts(order: any, store: any, printer: any): HTMLCanvasElement[] {
  const tableName = order.seatTableName || order.tableName || ''
  const seats = waiterSeats(order.items || [], tableName)
  const slips = order.waiterSlipCut === 'seat' ? seats.map((seat) => [seat]) : [seats]
  const placement = order.tableMarkPlacement
  return slips.map((seatList) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return canvas
    const fontSizeFactor = 1 + (printer?.font_increase_percentage || 0) / 100
    canvas.width = 576
    canvas.height = 10000
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#000000'
    let y = 32 + (printer?.empty_lines_on_top || 0) * 16
    y = drawHeader(ctx, y, order, store, fontSizeFactor)
    const tableNote = noteDisplay(order?.tableNote)
    if (tableNote) {
      const noteSize = Math.round(28 * fontSizeFactor)
      ctx.font = `normal ${noteSize}px Arial`
      ctx.textAlign = 'left'
      tableNote.split('\n').forEach((line) => {
        ctx.fillText(line, 10, y)
        y += noteSize + 4
      })
      drawLine(ctx, y)
      y += 16
    }
    const fontSize = Math.round(32 * fontSizeFactor)
    seatList.forEach((seat) => {
      if (seat.mark) y = drawBoldLeft(ctx, y, fontSize, seat.mark)
      if (seat.seatNote) {
        ctx.font = `normal ${fontSize - 4}px Arial`
        ctx.fillText(seat.seatNote, 32, y)
        y += fontSize
      }
      seat.items.forEach((item) => {
        const group: KitchenGroup = {
          name: item.name,
          amount: item.amount,
          price: 0,
          marks: placement ? item.marks : [],
          itemNote: item.itemNote,
          seatNote: '',
          options: item.options,
          category: '',
          item: null,
        }
        y = drawGroup(ctx, y, group, placement, fontSize, false)
      })
    })
    return finishCanvas(canvas, ctx, y + 24)
  })
}
